import React, { useEffect } from 'react'
import { FaInbox, FaRegFolderOpen } from 'react-icons/fa' // Import empty state icon
import { GET_CABS_BY_FLEET_OWNER } from '../../Services/cabapi/fleetggl'
import { useQuery } from '@apollo/client'
import moment from 'moment'
import { SkyButton } from '../../Components/Buttons'
import { useNavigate } from 'react-router-dom'
import { getDecryptedSessionStorageVariable } from '../../utils/encryptStorage'

const ManageCabs = () => {
  const navigate = useNavigate()
  const fleetId = getDecryptedSessionStorageVariable('fleetId')
  const { loading, error, data, refetch } = useQuery(GET_CABS_BY_FLEET_OWNER, {
    variables: {
      fleetOwnerId: fleetId,
    },
  })

  useEffect(() => {
    refetch()
  }, [])

  if (loading) {
    return <div>Loading...</div>
  }
  if (error) {
    return <div>Error: {error.message}</div>
  }

  // Function to calculate days remaining
  const calculateRemainingDays = (expiryDate) => {
    const today = moment().startOf('day') // Ensure we are comparing the start of the day
    const expiry = moment(expiryDate).startOf('day') // Ensure expiry date is also at the start of the day
    const daysRemaining = expiry.diff(today, 'days')

    return daysRemaining
  }

  return (
    <div>
      <div className="w-full h-full bg-white p-4 shadow rounded-md text-start">
        <div className="sticky top-0">
          <div className="text-stone-500 text-xl font-bold mb-4">
            Manage Cab
          </div>
          <div className="my-3 flex justify-end">
            <SkyButton label={`Add Cab`} onClick={() => navigate('add-cab')} />
          </div>
        </div>
        <div className="mt-4 w-full overflow-x-auto h-[70vh]">
          <table className="w-full text-sm text-left rtl:text-right text-gray-500 border-collapse border border-gray-300">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0 z-10">
              <tr className="sticky top-0">
                <th scope="col" className="px-6 py-3 border border-gray-300">
                  Car Owner Name
                </th>
                <th scope="col" className="px-6 py-3 border border-gray-300">
                  Car Number
                </th>
                <th scope="col" className="px-6 py-3 border border-gray-300">
                  Registration Date
                </th>
                <th scope="col" className="px-6 py-3 border border-gray-300">
                  Registration Expiry Date
                </th>
                <th scope="col" className="px-6 py-3 border border-gray-300">
                  Insurance Expiry Date
                </th>
                <th scope="col" className="px-6 py-3 border border-gray-300">
                  PUC Expiry Date
                </th>
                <th scope="col" className="px-6 py-3 border border-gray-300">
                  Color
                </th>
                <th scope="col" className="px-6 py-3 border border-gray-300">
                  Variant
                </th>
                <th scope="col" className="px-6 py-3 border border-gray-300">
                  Menu
                </th>
                <th scope="col" className="px-6 py-3 border border-gray-300">
                  Verification Status
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.getCabByFleetOwner?.length === 0 ? (
                <tr className="bg-white">
                  <td
                    className="px-2 py-4 border border-#cedbe2-800 text-center"
                    colSpan="13"
                  >
                    <div className="flex justify-center items-center h-16">
                      <FaInbox className="mr-2" />
                      No data found
                    </div>
                  </td>
                </tr>
              ) : (
                data?.getCabByFleetOwner?.map((items, index) => {
                  const regDaysRemaining = calculateRemainingDays(
                    items?.registrationExpiryDate
                  )
                  const insuranceDaysRemaining = calculateRemainingDays(
                    items?.insuranceExpiryDate
                  )
                  const pucDaysRemaining = calculateRemainingDays(
                    items?.pucExpiryDate
                  )

                  return (
                    <tr key={index} className="bg-white border-b">
                      <td className="px-6 py-4 border border-gray-300">
                        {items?.carOwnerName}
                      </td>
                      <td className="px-6 py-4 border border-gray-300">
                        {items?.carNo}
                      </td>
                      <td className="px-6 py-4 border border-gray-300">
                        {moment(items?.registrationDate).format('lll')}
                      </td>
                      <td className="px-6 py-4 border border-gray-300">
                        <div>
                          {moment(items?.registrationExpiryDate).format(
                            'DD-MMMM-YYYY'
                          )}
                          {regDaysRemaining <= 10 && regDaysRemaining >= 0 && (
                            <div className="flex gap-1 items-center">
                              <div className="w-2 h-2 rounded-full bg-red-700 animate-blink" />
                              <span className="text-red-400 text-[12px] whitespace-nowrap">
                                Renew Registration in {regDaysRemaining}{' '}
                                {regDaysRemaining === 1 ? 'day' : 'days'}
                              </span>
                            </div>
                          )}
                          {regDaysRemaining < 0 && (
                            <div className="flex gap-1 items-center">
                              <div className="w-2 h-2 rounded-full bg-red-700" />
                              <span className="text-red-400 text-[12px] whitespace-nowrap">
                                Registration Expired
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 border border-gray-300">
                        <div>
                          {moment(items?.insuranceExpiryDate).format(
                            'DD-MMMM-YYYY'
                          )}
                          {insuranceDaysRemaining <= 10 &&
                            insuranceDaysRemaining >= 0 && (
                              <div className="flex gap-1 items-center">
                                <div>
                                  <div className="w-2 h-2 rounded-full bg-red-700 animate-blink" />
                                </div>
                                <div>
                                  <span className="text-red-400 text-[12px] whitespace-nowrap">
                                    Renew Insurance in {insuranceDaysRemaining}{' '}
                                    {insuranceDaysRemaining === 1
                                      ? 'day'
                                      : 'days'}
                                  </span>
                                </div>
                              </div>
                            )}
                          {insuranceDaysRemaining < 0 && (
                            <div className="flex gap-1 items-center">
                              <div className="w-2 h-2 rounded-full bg-red-700" />
                              <span className="text-red-400 text-[12px] whitespace-nowrap">
                                Insurance Expired
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 border border-gray-300">
                        <div>
                          {moment(items?.pucExpiryDate).format('DD-MMMM-YYYY')}
                          {pucDaysRemaining <= 10 && pucDaysRemaining >= 0 && (
                            <div className="flex gap-1 items-center">
                              <div className="w-2 h-2 rounded-full bg-red-700 animate-blink" />
                              <span className="text-red-400 text-[12px] whitespace-nowrap">
                                Renew PUC in {pucDaysRemaining}{' '}
                                {pucDaysRemaining === 1 ? 'day' : 'days'}
                              </span>
                            </div>
                          )}
                          {pucDaysRemaining < 0 && (
                            <div className="flex gap-1 items-center">
                              <div className="w-2 h-2 rounded-full bg-red-700" />
                              <span className="text-red-400 text-[12px] whitespace-nowrap">
                                PUC Expired
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 border border-gray-300">
                        {items?.color}
                      </td>
                      <td className="px-6 py-4 border border-gray-300">
                        {items?.variant}
                      </td>
                      <td className="px-6 py-4 border border-gray-300">
                        <SkyButton
                          label={'Edit'}
                          onClick={() => {
                            navigate('/manage-cab/edit-cab', {
                              state: items,
                            })
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 border border-gray-300">
                        {items?.isVerified ? 'Verified' : 'Un-Verified'}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default ManageCabs
